<template>
  <div class="table-responsive">
    <table class="table table-hover">
      <thead>
      <tr>
        <th>{{ $t('short_term_application.available_exchanges.column.country') }}</th>
        <th>{{ $t('short_term_application.available_exchanges.column.category') }}</th>
        <th>{{ $t('short_term_application.available_exchanges.column.languages') }}</th>
        <th>{{ $t('short_term_application.available_exchanges.column.functions') }}</th>
        <th>{{ $t('short_term_application.available_exchanges.column.available_places') }}</th>
        <th>{{ $t('short_term_application.available_exchanges.column.length') }}</th>
        <th>{{ $t('short_term_application.available_exchanges.column.specialisation') }}</th>
        <th>{{ $t('short_term_application.available_exchanges.column.additional_information') }}</th>
      </tr>
      </thead>
      <tbody>
        <tr class="filter no-hover">
          <td>
            <grid-filter-list
              :show-dropdown-caret="false"
              :filter="countryFilter"
              :model-value="countryFilter.selectedValueArray"
              @update:model-value="onFilterChanged(countryFilter, $event)"
            >
              {{ filterTitle(countryFilter) }}
            </grid-filter-list>
          </td>
          <td>
            <grid-filter-list
              :show-dropdown-caret="false"
              :filter="categoryFilter"
              :model-value="categoryFilter.selectedValueArray"
              @update:model-value="onFilterChanged(categoryFilter, $event)"
            >
              {{ filterTitle(categoryFilter) }}
            </grid-filter-list>
          </td>
          <td>
            <grid-filter-list
              :show-dropdown-caret="false"
              :filter="languageFilter"
              :model-value="languageFilter.selectedValueArray"
              @update:model-value="onFilterChanged(languageFilter, $event)"
            >
              {{ filterTitle(languageFilter) }}
            </grid-filter-list>
          </td>
          <td>
            <grid-filter-list
              :show-dropdown-caret="false"
              :filter="functionFilter"
              :model-value="functionFilter.selectedValueArray"
              @update:model-value="onFilterChanged(functionFilter, $event)"
            >
              {{ filterTitle(functionFilter) }}
            </grid-filter-list>
          </td>
          <td></td>
          <td>
            <grid-filter-list
              :show-dropdown-caret="false"
              :filter="durationFilter"
              :model-value="durationFilter.selectedValueArray"
              @update:model-value="onFilterChanged(durationFilter, $event)"
            >
              {{ filterTitle(durationFilter) }}
            </grid-filter-list>
          </td>
          <td>
            <grid-filter-list
              :show-dropdown-caret="false"
              :filter="specialisationFilter"
              :model-value="specialisationFilter.selectedValueArray"
              @update:model-value="onFilterChanged(specialisationFilter, $event)"
            >
              {{ filterTitle(specialisationFilter) }}
            </grid-filter-list>
          </td>
          <td></td>
        </tr>
        <tr
          v-for="place of filteredPlaces"
          :key="place.id"
        >
          <td>{{ place.country }}</td>
          <td>{{ place.category }}</td>
          <td>{{ place.languages }}</td>
          <td>{{ place.functions }}</td>
          <td>{{ place.places }}</td>
          <td>{{ place.length }}</td>
          <td>{{ place.specialisation }}</td>
          <td>{{ place.additionalInformation }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import {ref, computed, onMounted} from 'vue'
import GridFilterList from "@/js/vue-components/grid-filter-list.vue";
import { GridFilter } from "@/js/vue-components/types";
import {createFilter, filterItems} from "@/js/vue-components/filter-utils";
import {isNonEmptyString} from "@/js/js-utils";

interface AvailablePlace {
  id: string;
  additionalInformation: string
  category: string;
  country: string;
  exchangeType: string;
  functions: string;
  languages: string;
  length: string;
  places: number;
  specialisation: string;
}

const props = defineProps({
  availablePlaces: {default: (): AvailablePlace[] => [], type: Array as () => AvailablePlace[]},
})

const countryFilter = ref(createFilter([]))
const categoryFilter = ref(createFilter([]))
const languageFilter = ref(createFilter([]))
const functionFilter = ref(createFilter([]))
const durationFilter = ref(createFilter([]))
const specialisationFilter = ref(createFilter([]))

const filteredPlaces = computed((): AvailablePlace[] => {
  const selectedCountries = filterItems(countryFilter.value)
  const selectedCategories = filterItems(categoryFilter.value)
  const selectedLanguages = filterItems(languageFilter.value)
  const selectedFunctions = filterItems(functionFilter.value)
  const selectedDurations = filterItems(durationFilter.value)
  const selectedSpecialisations = filterItems(specialisationFilter.value)

  return props.availablePlaces.filter((f) => {
    if (selectedCountries.length > 0 && !selectedCountries.includes(f.country)) {
      return false
    }

    if (selectedCategories.length > 0 && !selectedCategories.includes(f.category)) {
      return false
    }

    if (selectedLanguages.length > 0) {
      const placeLanguages = f.languages.split(', ')
      if (!selectedLanguages.find(l => placeLanguages.includes(l))) {
        return false
      }
    }

    if (selectedFunctions.length > 0) {
      const placeFunctions = f.functions.split(', ')
      if (!selectedFunctions.find(l => placeFunctions.includes(l))) {
        return false
      }
    }

    if (selectedDurations.length > 0) {
      const placeDurations = f.length.split(', ')
      if (!selectedDurations.find(l => placeDurations.includes(l))) {
        return false
      }
    }

    if (selectedSpecialisations.length > 0 && !selectedSpecialisations.includes(f.specialisation || '')) {
      return false
    }

    return true
  })
})

const updateAvailablePlacesFilters = (): void => {
  const countries: string[] = []
  const categories: string[] = []
  let languages: string[] = []
  let functions: string[] = []
  const specialisations: string[] = []
  let durations: string[] = []

  for (const place of props.availablePlaces) {
    countries.push(place.country)
    categories.push(place.category)
    languages = languages.concat(place.languages.split(', '))
    functions = functions.concat(place.functions.split(', '))
    specialisations.push(place.specialisation)
    durations = durations.concat(place.length.split(', '))
  }

  countryFilter.value = createFilter(countries)
  categoryFilter.value = createFilter(categories)
  languageFilter.value = createFilter(languages)
  functionFilter.value = createFilter(functions)
  specialisationFilter.value = createFilter(specialisations)
  durationFilter.value = createFilter(durations)
}

const onFilterChanged = (filter: GridFilter, filterValue: string | string[]): void => {
  filter.selectedValue = filterValue
}

const filterTitle = (filter: GridFilter): string => {
  const title = filter.selectedTitle
  if (isNonEmptyString(title)) {
    return title
  }

  return 'All'
}

onMounted(() => {
  updateAvailablePlacesFilters()
})
</script>
